import { ComponentProps, KeyboardEvent, MouseEvent, ReactElement, ReactNode } from "react";
import { To, useNavigate, useResolvedPath } from "react-router-dom";
import { NavigateOptions } from "react-router/dist/lib/context";
import { useToggle } from "react-use";
import { cva } from "class-variance-authority";
import { ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "~/lib/utils";
import { Card } from "./card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./collapsible";
import { Skeleton } from "./skeleton";

const ListCardVariants = {
  listItem: cva("border-gray-200 not-first-of-type:[&_.row-content]:border-t", {
    variants: {
      hoverable: {
        true: "[&:not(:has(:nth-child(2):hover)):hover]:[&_.row-content]:border-transparent [&:not([aria-expanded='true']):hover+*]:[&_.row-content]:border-transparent",
        false: "",
      },
    },
  }),
  ctaSummary: cva([
    "cursor-pointer rounded-lg flex overflow-hidden gap-4 w-full transition-colors select-none hover:bg-gray-200",
    "not-disabled:active:bg-gray-300 not-disabled:cursor-pointer",
    "disabled:cursor-default disabled:opacity-80 disabled:hover:bg-gray-100 disabled:hover:border-transparent disabled:hover:opacity-100",
    "aria-disabled:cursor-default aria-disabled:opacity-80 aria-disabled:hover:bg-gray-100 aria-disabled:hover:border-transparent aria-disabled:hover:opacity-100",
  ]),
};

// ==================
// LoadingState
// ==================

export interface LoadingStateProps {
  className?: string;
  count?: number;
}

const LoadingState = ({ className, count = 6 }: LoadingStateProps) => {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <Skeleton
          key={i}
          asChild
          className={cn("not-last-of-type:mb-2 bg-gray-300 h-[60px]", className)}
        >
          <li />
        </Skeleton>
      ))}
    </>
  );
};

// ==================
// ListItemContent
// ==================

interface ListItemContentProps extends ComponentProps<"div"> {
  className?: string;
  rowContentClassName?: string;
  icon?: ReactElement;
}

const ListItemContent = ({
  className,
  rowContentClassName,
  children,
  icon,
  ...props
}: ListItemContentProps) => (
  <div
    className={cn(
      "rounded-lg overflow-hidden flex items-center px-2.5 grow shrink basis-0",
      className
    )}
    {...props}
  >
    {icon && (
      <div className={cn("flex items-center shrink-0 justify-center size-8 mr-2.5")}>{icon}</div>
    )}
    <div className={cn("row-content flex items-center py-2.5 grow", rowContentClassName)}>
      {children}
    </div>
  </div>
);

// ==================
// ExpandableListItem
// ==================

export interface ExpandableListItemProps extends ListItemContentProps {
  expansionContent: ReactNode;
  disabled?: boolean;
  indicator?: boolean;
}

export const ExpandableListItem = ({
  expansionContent,
  icon,
  disabled,
  className,
  children,
  indicator = false,
}: ExpandableListItemProps) => {
  const [expanded, toggleExpanded] = useToggle(false);

  const handleKeyDown = (event: KeyboardEvent) => {
    if (disabled) {
      return event.preventDefault();
    }

    if (event.key === "Enter") {
      toggleExpanded();
    }
  };

  return (
    <Collapsible open={expanded} onOpenChange={disabled ? undefined : toggleExpanded} asChild>
      <li
        aria-expanded={expanded}
        className={cn(ListCardVariants.listItem({ hoverable: true }), className)}
      >
        <CollapsibleTrigger
          asChild
          role="button"
          className={cn(ListCardVariants.ctaSummary())}
          tabIndex={disabled ? -1 : 0}
          disabled={disabled}
          aria-disabled={disabled}
          onKeyDown={handleKeyDown}
        >
          <ListItemContent icon={icon}>
            {children}
            {indicator && (
              <div className="flex ml-auto opacity-70">
                {expanded ? <ChevronUp className="size-5" /> : <ChevronDown className="size-5" />}
              </div>
            )}
          </ListItemContent>
        </CollapsibleTrigger>
        <CollapsibleContent animated>
          <div className="px-4 py-2">{expansionContent}</div>
        </CollapsibleContent>
      </li>
    </Collapsible>
  );
};

// ==================
// ListItem
// ==================

export type ListItemLinkProps<T extends "link" | "default" = "link"> = T extends "link"
  ? {
      to: To;
      navOptions?: NavigateOptions;
    }
  : {
      to?: undefined;
      navOptions?: undefined;
    };

export type ListItemDefaultProps<T extends "link" | "default" = "default"> = T extends "default"
  ? {
      onClick?: (event?: MouseEvent) => void;
    }
  : {
      onClick?: undefined;
    };

export type ListItemProps<T extends "link" | "default"> = ListItemContentProps & {
  disabled?: boolean;
  testId?: string;
  type: T;
} & ListItemDefaultProps<T> &
  ListItemLinkProps<T>;

export const ListItem = <T extends "link" | "default" = "default">({
  type = "default" as T,
  onClick,
  to,
  disabled,
  navOptions,
  icon,
  className,
  rowContentClassName,
  children,
  testId,
}: ListItemProps<T>) => {
  const navigate = useNavigate();
  const { pathname, search } = useResolvedPath(to ?? "", navOptions);

  const handleClick = (event?: MouseEvent) => {
    if (disabled) {
      return event?.preventDefault();
    }

    if (type === "link") {
      // Check if Command (metaKey) or Alt (altKey) is pressed on macOS/Windows/Linux
      if (event?.metaKey || event?.altKey) {
        // Open in a new tab using resolved url
        return window.open(pathname + search, "_blank");
      }

      return navigate(to!, navOptions);
    }

    onClick?.(event);
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (disabled) {
      return event.preventDefault();
    }

    if (event.key === "Enter") {
      handleClick();
    }
  };

  return (
    <li
      role={type === "link" ? "link" : "listitem"}
      className={cn(
        ListCardVariants.listItem({ hoverable: true }),
        ListCardVariants.ctaSummary(),
        className
      )}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      data-testid={testId}
    >
      <ListItemContent icon={icon} rowContentClassName={rowContentClassName}>
        {children}
      </ListItemContent>
    </li>
  );
};

// ==================
// ListCard
// ==================

interface ListCardProps {
  borderTop?: boolean;
  children?: ReactNode;
  footer?: ReactElement | null;
  header?: ReactElement | null;
  loading?: boolean;
  placeholder?: LoadingStateProps;
  className?: string;
  listClassName?: string;
}

export const ListCard = ({
  borderTop = false,
  children = null,
  footer = null,
  header = null,
  loading = false,
  placeholder = {},
  className,
  listClassName,
}: ListCardProps) => {
  return (
    <Card variant="flat" className={cn("overflow-hidden", className)}>
      {header}
      <ul
        className={cn(
          "p-2.5 list-none m-0 overflow-auto",
          borderTop &&
            !loading &&
            "[&_li:nth-child(1)]:border-t [&_li:nth-child(1):hover]:border-transparent",
          listClassName
        )}
      >
        {loading ? <LoadingState {...placeholder} /> : children}
      </ul>
      {footer}
    </Card>
  );
};
